import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { graphqlClient } from "./graphql";
import { graphql } from "@/__generated__";
import type { PuzzleExerciseInput as PuzzleExerciseInputGql } from "@/__generated__/graphql";
import type { Exercise, ExerciseType } from "@/types/puzzle";
import type { DeckNameParts } from "@/lib/flashcardLabels";

// ───────────────────────────── Mapping ─────────────────────────────
// The frontend Exercise uses a kebab-case `type` and flat fields; the backend
// uses a SCREAMING_SNAKE enum + a `payload` object (id/type are columns, not
// payload keys). These convert between the two — the single source of truth so
// the builder and player never hand-roll the translation.

export function exerciseTypeToEnum(type: ExerciseType): string {
  return type.replace(/-/g, "_").toUpperCase();
}

export function exerciseTypeFromEnum(value: string): ExerciseType {
  return value.toLowerCase().replace(/_/g, "-") as ExerciseType;
}

export interface PuzzleExerciseInput {
  id?: string;
  exerciseType: string;
  payload: Record<string, unknown>;
  position: number;
}

// Exercise → savePuzzleContent input. The payload is everything except the
// id/type columns. A client-only id (non-UUID) is harmless: the server scopes
// id lookups to the puzzle, so it just creates a fresh exercise.
export function toExerciseInput(exercise: Exercise, position: number): PuzzleExerciseInput {
  const { id, type, ...payload } = exercise as Exercise & { id: string; type: ExerciseType };
  return { id, exerciseType: exerciseTypeToEnum(type), payload, position };
}

// Owner builder node ({id, exerciseType, payload}) → frontend Exercise.
export function fromOwnerExercise(node: {
  id: string;
  exerciseType: string;
  payload: Record<string, unknown>;
}): Exercise {
  return { id: node.id, type: exerciseTypeFromEnum(node.exerciseType), ...node.payload } as Exercise;
}

// ───────────────────────────── Types ─────────────────────────────

export interface EligibleStudent {
  id: string;
  name: string;
  initials: string;
  languages: string[];
}

export interface PuzzleVocabEntry {
  id: string;
  word: string;
  context: string | null;
  language: string | null;
  state: string;
}

export interface AssignedPuzzle {
  id: string;
  name: string;
  theme: string;
  exerciseCount: number;
  status: string;
  language: { id: string; name: string };
  level?: number | null;
  official?: boolean;
  cefrLevel?: string | null;
}

export interface PlayableExercise {
  id: string;
  exerciseType: string;
  data: Record<string, unknown>;
}

export interface StartedAttempt {
  id: string;
  puzzleId: string;
  name: string;
  theme: string;
  exercises: PlayableExercise[];
}

export interface GradedResult {
  exerciseId: string;
  exerciseType: string;
  correct: boolean;
  solution: Record<string, unknown>;
}

export interface PuzzleAnswer {
  exerciseId: string;
  answer: unknown;
}

// ───────────────────────────── Queries ─────────────────────────────

const ELIGIBLE_STUDENTS_QUERY = graphql(`
  query EligibleStudentsForPuzzles {
    eligibleStudentsForPuzzles { id name initials languages }
  }
`);

export function useEligibleStudents() {
  const { data, isLoading } = useQuery({
    queryKey: ["puzzles", "eligibleStudents"],
    queryFn: async () => {
      const r = await graphqlClient.request(ELIGIBLE_STUDENTS_QUERY);
      return r.eligibleStudentsForPuzzles;
    },
    staleTime: 60_000,
  });
  return { students: data ?? [], isLoading };
}

const STUDENT_VOCAB_QUERY = graphql(`
  query StudentVocabForPuzzle($studentProfileId: ID!) {
    studentVocabForPuzzle(studentProfileId: $studentProfileId) { id word context language state }
  }
`);

export function useStudentVocab(studentProfileId: string | null | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ["puzzles", "studentVocab", studentProfileId],
    queryFn: async () => {
      if (!studentProfileId) return [] as PuzzleVocabEntry[];
      const r = await graphqlClient.request(
        STUDENT_VOCAB_QUERY,
        { studentProfileId },
      );
      return r.studentVocabForPuzzle as PuzzleVocabEntry[];
    },
    enabled: Boolean(studentProfileId),
    staleTime: 60_000,
  });
  return { vocab: data ?? [], isLoading };
}

const MY_ASSIGNED_PUZZLES_QUERY = graphql(`
  query MyAssignedPuzzles {
    myAssignedPuzzles { id name theme exerciseCount status language { id name } }
  }
`);

export function useMyAssignedPuzzles() {
  const { data, isLoading } = useQuery({
    queryKey: ["puzzles", "myAssigned"],
    queryFn: async () => {
      const r = await graphqlClient.request(MY_ASSIGNED_PUZZLES_QUERY);
      return r.myAssignedPuzzles;
    },
    staleTime: 30_000,
  });
  return { puzzles: data ?? [], isLoading };
}

// The parts flashcardDeckName composes the picker label from; the server's
// English displayName is deliberately not selected (9.3-E).
export interface FlashcardDeckOption extends DeckNameParts {
  id: string;
}

const MY_DECKS_QUERY = graphql(`
  query MyFlashcardDecksForPractice {
    myFlashcardDecks {
      id
      personal
      targetLanguage { id code name }
      tutorProfile { id user { firstName } }
    }
  }
`);

// The student's decks — for the "practice a deck" picker.
export function useMyFlashcardDecks() {
  const { data } = useQuery({
    queryKey: ["puzzles", "decks"],
    queryFn: async () => (await graphqlClient.request(MY_DECKS_QUERY)).myFlashcardDecks,
    staleTime: 60_000,
  });
  return { decks: data ?? [] };
}

const MY_GENERATED_QUERY = graphql(`
  query MyGeneratedPuzzles {
    myGeneratedPuzzles { id name theme exerciseCount status cefrLevel language { id name } }
  }
`);

// The student's own flashcard-derived practice packs (Phase B). Empty unless both
// flags are on server-side.
export function useMyGeneratedPuzzles() {
  const { data, isLoading } = useQuery({
    queryKey: ["puzzles", "generated"],
    queryFn: async () => (await graphqlClient.request(MY_GENERATED_QUERY)).myGeneratedPuzzles,
    staleTime: 30_000,
  });
  return { puzzles: data ?? [], isLoading };
}

const GENERATE_PRACTICE = graphql(`
  mutation GenerateFlashcardPractice($mode: String!, $deckId: ID) {
    generateFlashcardPractice(input: { mode: $mode, deckId: $deckId }) {
      puzzle { id }
      errorCode
      errors
    }
  }
`);

// A refused generation, carrying the server's stable code so the page can say
// why in the student's language (practiceErrorKey); the message is the English
// fallback.
export class PracticeGenerationError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null) {
    super(message);
    this.name = "PracticeGenerationError";
    this.code = code;
  }
}

export const PRACTICE_ERROR_CODES = ["unavailable", "no_mistakes", "not_enough_cards", "deck_not_found"] as const;

// Anything that is not a known refusal — a transport failure included — reads
// as the generic line.
export function practiceErrorKey(error: unknown): string {
  const code = error instanceof PracticeGenerationError ? error.code : null;
  return (PRACTICE_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? `puzzleStudent.list.practiceErrors.${code}`
    : "puzzleStudent.list.practiceErrors.generic";
}

// Generate a practice pack from the student's flashcards ("mistakes" | "deck").
// Returns the new pack id (throws a PracticeGenerationError on a refusal) so
// the caller can drop straight into the player.
export function useGenerateFlashcardPractice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ mode, deckId }: { mode: "mistakes" | "deck"; deckId?: string }) => {
      const r = await graphqlClient.request(
        GENERATE_PRACTICE,
        { mode, deckId: deckId ?? null },
      );
      if (r.generateFlashcardPractice.errors.length) {
        throw new PracticeGenerationError(r.generateFlashcardPractice.errors[0], r.generateFlashcardPractice.errorCode ?? null);
      }
      return r.generateFlashcardPractice.puzzle!.id;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["puzzles", "generated"] }),
  });
}

const RECOMMENDED_PUZZLES_QUERY = graphql(`
  query RecommendedPuzzles {
    recommendedPuzzles { id name theme exerciseCount status official cefrLevel language { id name } }
  }
`);

// The merged "Recommended for you" rail: official starter + community packs in
// the student's languages, ordered server-side by CEFR fit (slightly above the
// student's declared level), official-first. Empty unless both flags are on.
export function useRecommendedPuzzles() {
  const { data, isLoading } = useQuery({
    queryKey: ["puzzles", "recommended"],
    queryFn: async () => {
      const r = await graphqlClient.request(RECOMMENDED_PUZZLES_QUERY);
      return r.recommendedPuzzles;
    },
    staleTime: 60_000,
  });
  return { puzzles: data ?? [], isLoading };
}

// ───────────────────────────── Journey ─────────────────────────────
export type JourneyNodeState = "COMPLETED" | "ACTIVE" | "AVAILABLE" | "LOCKED";

export interface JourneyNode {
  puzzleId: string;
  name: string;
  theme: string;
  cefrLevel: string | null;
  exerciseCount: number;
  state: JourneyNodeState;
  stars: number;
  bestScorePct: number | null;
  isBandEntry: boolean;
  isMilestone: boolean;
}

export interface JourneyBand {
  band: string;
  totalCount: number;
  completedCount: number;
  progressPct: number;
  nodes: JourneyNode[];
}

export interface PuzzleJourney {
  language: { id: string; name: string; code: string };
  currentBand: string | null;
  overallProgressPct: number;
  availableLanguages: { id: string; name: string; code: string }[];
  bands: JourneyBand[];
}

const PUZZLE_JOURNEY_QUERY = graphql(`
  query PuzzleJourney($languageId: ID) {
    puzzleJourney(languageId: $languageId) {
      currentBand
      overallProgressPct
      language { id name code }
      availableLanguages { id name code }
      bands {
        band totalCount completedCount progressPct
        nodes { puzzleId name theme cefrLevel exerciseCount state stars bestScorePct isBandEntry isMilestone }
      }
    }
  }
`);

// The gamified journey for one language: official packs as CEFR-band linear
// locked tracks with per-pack state + stars. Null unless both flags are on.
export function usePuzzleJourney(languageId?: string | null) {
  const { data, isLoading } = useQuery({
    queryKey: ["puzzles", "journey", languageId ?? "default"],
    queryFn: async () =>
      (await graphqlClient.request(PUZZLE_JOURNEY_QUERY, { languageId: languageId ?? null }))
        .puzzleJourney as PuzzleJourney | null,
    staleTime: 30_000,
  });
  return { journey: data ?? null, isLoading };
}

// ───────────────────────────── Mutations ─────────────────────────────

const CREATE_PUZZLE = graphql(`
  mutation CreatePuzzle($studentProfileId: ID!, $languageId: ID!, $name: String!, $theme: String!) {
    createPuzzle(input: {studentProfileId: $studentProfileId, languageId: $languageId, name: $name, theme: $theme}) {
      puzzle { id }
      errors
    }
  }
`);

const SAVE_PUZZLE_CONTENT = graphql(`
  mutation SavePuzzleContent($puzzleId: ID!, $exercises: [PuzzleExerciseInput!]!) {
    savePuzzleContent(input: {puzzleId: $puzzleId, exercises: $exercises}) {
      puzzle { id }
      errors
    }
  }
`);

const PUBLISH_PUZZLE = graphql(`
  mutation PublishPuzzle($puzzleId: ID!) {
    publishPuzzle(input: {puzzleId: $puzzleId}) {
      puzzle { id status }
      errors
    }
  }
`);

interface CreateArgs {
  studentProfileId: string;
  languageId: string;
  name: string;
  theme: string;
}

// Build → save → publish, the whole pack in one call. Returns the new puzzle id
// or throws with the first server error.
export function usePublishPuzzlePack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ meta, exercises }: { meta: CreateArgs; exercises: Exercise[] }) => {
      const created = await graphqlClient.request(
        CREATE_PUZZLE,
        { studentProfileId: meta.studentProfileId, languageId: meta.languageId, name: meta.name, theme: meta.theme },
      );
      if (created.createPuzzle.errors.length) throw new Error(created.createPuzzle.errors[0]);
      const puzzleId = created.createPuzzle.puzzle!.id;

      const saved = await graphqlClient.request(SAVE_PUZZLE_CONTENT, {
        puzzleId,
        // toExerciseInput yields runtime-correct values (enum strings + JSON
        // payload); map to the generated input type, which differs only nominally.
        exercises: exercises.map((ex, i) => toExerciseInput(ex, i)) as unknown as PuzzleExerciseInputGql[],
      });
      if (saved.savePuzzleContent.errors.length) throw new Error(saved.savePuzzleContent.errors[0]);

      const published = await graphqlClient.request(PUBLISH_PUZZLE, { puzzleId });
      if (published.publishPuzzle.errors.length) throw new Error(published.publishPuzzle.errors[0]);
      return puzzleId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["puzzles", "myAssigned"] });
    },
  });
}

const GENERATE_EXERCISES = graphql(`
  mutation GeneratePuzzleExercises($studentProfileId: ID!, $theme: String!, $count: Int) {
    generatePuzzleExercises(input: {studentProfileId: $studentProfileId, theme: $theme, count: $count}) {
      exercises { exerciseType payload }
      errors
    }
  }
`);

// AI-generate draft exercises for the builder. Returns frontend Exercises ready
// to drop into the pack (the tutor still reviews/edits/publishes).
export function useGeneratePuzzleExercises() {
  return useMutation({
    mutationFn: async ({ studentProfileId, theme, count }: { studentProfileId: string; theme: string; count: number }) => {
      const r = await graphqlClient.request(GENERATE_EXERCISES, { studentProfileId, theme, count });
      if (r.generatePuzzleExercises.errors.length) throw new Error(r.generatePuzzleExercises.errors[0]);
      return r.generatePuzzleExercises.exercises.map((g) =>
        fromOwnerExercise({ id: "ai", exerciseType: g.exerciseType, payload: g.payload }),
      );
    },
  });
}

const START_ATTEMPT = graphql(`
  mutation StartPuzzleAttempt($puzzleId: ID!) {
    startPuzzleAttempt(input: {puzzleId: $puzzleId}) {
      attempt { id puzzleId name theme exercises { id exerciseType data } }
      errors
    }
  }
`);

export function useStartPuzzleAttempt() {
  return useMutation({
    mutationFn: async (puzzleId: string) => {
      const r = await graphqlClient.request(
        START_ATTEMPT,
        { puzzleId },
      );
      if (r.startPuzzleAttempt.errors.length) throw new Error(r.startPuzzleAttempt.errors[0]);
      return r.startPuzzleAttempt.attempt!;
    },
  });
}

const GRADE_ANSWER = graphql(`
  mutation GradePuzzleAnswer($attemptId: ID!, $exerciseId: ID!, $answer: Json!) {
    gradePuzzleAnswer(input: {attemptId: $attemptId, exerciseId: $exerciseId, answer: $answer}) {
      correct solution completed score total xpAwarded
      gamification { totalXp level currentStreakDays longestStreakDays dailyGoalXp xpToday goalMet }
      errors
    }
  }
`);

export interface GamificationSnapshot {
  totalXp: number;
  level: number;
  currentStreakDays: number;
  longestStreakDays: number;
  dailyGoalXp: number;
  xpToday: number;
  goalMet: boolean;
}

export interface GradeResponse {
  correct: boolean;
  solution: Record<string, unknown>;
  completed: boolean;
  score: number;
  total: number;
  xpAwarded: number;
  gamification: GamificationSnapshot | null;
}

// Grade a single answer mid-attempt (per-question feedback). The solution is
// revealed only in the response, post-grade.
export function useGradePuzzleAnswer() {
  return useMutation({
    mutationFn: async ({ attemptId, exerciseId, answer }: { attemptId: string; exerciseId: string; answer: unknown }) => {
      const r = await graphqlClient.request(
        GRADE_ANSWER,
        { attemptId, exerciseId, answer },
      );
      if (r.gradePuzzleAnswer.errors.length) throw new Error(r.gradePuzzleAnswer.errors[0]);
      const { correct, solution, completed, score, total, xpAwarded, gamification } = r.gradePuzzleAnswer;
      return { correct, solution, completed, score, total, xpAwarded, gamification } as GradeResponse;
    },
  });
}

const SUBMIT_ATTEMPT = graphql(`
  mutation SubmitPuzzleAttempt($attemptId: ID!, $answers: [PuzzleAnswerInput!]!) {
    submitPuzzleAttempt(input: {attemptId: $attemptId, answers: $answers}) {
      score total
      results { exerciseId exerciseType correct solution }
      errors
    }
  }
`);

export interface SubmitResult {
  score: number;
  total: number;
  results: GradedResult[];
}

export function useSubmitPuzzleAttempt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ attemptId, answers }: { attemptId: string; answers: PuzzleAnswer[] }) => {
      const r = await graphqlClient.request(SUBMIT_ATTEMPT, { attemptId, answers });
      if (r.submitPuzzleAttempt.errors.length) throw new Error(r.submitPuzzleAttempt.errors[0]);
      const { score, total, results } = r.submitPuzzleAttempt;
      return { score, total, results } as SubmitResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["puzzles", "myAssigned"] });
      // A finished pack unlocks the next journey node and moves XP / streak:
      // both are cached for 30s, so without this the list shows the old state
      // right after the student goes back to it (#1111).
      queryClient.invalidateQueries({ queryKey: ["puzzles", "journey"] });
      queryClient.invalidateQueries({ queryKey: ["puzzles", "gamification"] });
    },
  });
}
